import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { foodRun } from 'src/backend';
import moment from 'moment';
import useRecipientOrganizations from 'src/hooks/useRecipientOrganizations';

const ChangeRecipientModal = ({
  open,
  onClose,
  donationId,
  currentRecipientTeamId,
  currentRecipientName,
  endDate,
  onSuccess,
}) => {
  const { t } = useTranslation('translation');
  const [selectedRecipientId, setSelectedRecipientId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    recipients,
    loading: loadingRecipients,
    error: recipientsError,
  } = useRecipientOrganizations({ open, currentRecipientTeamId });

  // Calculate days since scheduled end date
  const daysSinceScheduled = moment().diff(moment(endDate), 'days');
  const canChange = daysSinceScheduled <= 30;

  // Reset selected recipient when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedRecipientId('');
      setError('');
    }
  }, [open]);

  // Set error from hook if it exists
  useEffect(() => {
    if (recipientsError) {
      setError(recipientsError);
    }
  }, [recipientsError]);

  const handleChange = async () => {
    if (!selectedRecipientId) {
      setError(t('donations.change-recipient-modal.select-recipient-error'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await foodRun.changeRecipientRetroactively({
        donationId,
        newRecipientTeamId: selectedRecipientId,
      });

      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      setError(
        err.message || t('donations.change-recipient-modal.change-failed-error')
      );
    } finally {
      setLoading(false);
    }
  };

  if (!canChange) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('donations.change-recipient-modal.cannot-change-title')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography>
              {t('donations.change-recipient-modal.cannot-change-message', {
                daysSinceScheduled,
              })}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('donations.close')}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('donations.change-recipient-modal.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              {t('donations.change-recipient-modal.info-message', {
                daysSinceScheduled,
              })}
            </Typography>
          </Alert>

          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>
              {t('donations.change-recipient-modal.current-recipient')}:
            </strong>{' '}
            {currentRecipientName}
          </Typography>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>
              {t('donations.change-recipient-modal.new-recipient')}
            </InputLabel>
            <Select
              value={selectedRecipientId}
              onChange={(e) => setSelectedRecipientId(e.target.value)}
              label={t('donations.change-recipient-modal.new-recipient')}
              disabled={loadingRecipients}
            >
              {recipients.map((recipient) => (
                <MenuItem key={recipient.team_id} value={recipient.team_id}>
                  {recipient.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <LoadingButton
          onClick={handleChange}
          loading={loading}
          variant="contained"
          disabled={!selectedRecipientId}
        >
          {t('donations.change-recipient-modal.change-recipient')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeRecipientModal;
